import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { addDays, departmentLabel, kindLabel, specialLabel, teamError, teamPost } from './teamFormat';
import { ClientField, Panel, StaffSelect } from './TeamParts';
import TeamBalanceTab from './TeamBalanceTab';
import TeamImportTab from './TeamImportTab';
import TeamStaffPanel from './TeamStaffPanel';
import TeamMissingCertPanel from './TeamMissingCertPanel';

const SPECIAL_CODES = ['KLEIN_VERLET', 'GEBOORTE', 'ADOPTIE', 'OUDERSCHAP', 'FAMILIAAL', 'ZORG', 'MANTELZORG', 'OPLEIDING', 'ONBETAALD', 'ANDERE'];
const field = { '& .MuiInputBase-root': { height: 40 } };

/**
 * Management (and Els): balances of everyone and the leave right, entries
 * nobody can request (installations with a client, half days and hours,
 * moving home days) and the Excel import.
 */
const TeamAdminTab = (props) => {
    const { t, theme, me } = props;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {me.rights.manage_staff && <TeamStaffPanel {...props} />}
            {me.rights.sees_all && <TeamMissingCertPanel {...props} />}
            {me.rights.sees_all && (
                <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>{t('team_admin_balances', 'Balances and leave right')}</Typography>
                    <TeamBalanceTab {...props} showOwn={false} />
                </Box>
            )}
            {me.rights.manager && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                    <InstallPanel {...props} />
                    <HoursPanel {...props} />
                    <HomePanel {...props} />
                    <GooglePanel {...props} />
                </Box>
            )}
            {me.rights.import && (
                <Panel theme={theme} title={t('team_tab_import', 'Excel import')}>
                    <TeamImportTab {...props} />
                </Panel>
            )}
        </Box>
    );
};

/** Installation or intervention for a colleague, linked to a SoftTouch Vlaanderen client. */
const InstallPanel = ({ t, theme, token, me, staff, notify, onChanged }) => {
    const [who, setWho] = useState('');
    const [kind, setKind] = useState('INSTALL');
    const [day, setDay] = useState(addDays(me.today, 1));
    const [client, setClient] = useState(null);
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);

    const save = () => {
        setBusy(true);
        teamPost('enter', token, { staff_id: who, kind, from: day, to: day, remark, lic_client_id: client ? client.id : null }).then((response) => {
            setBusy(false);
            if (response?.success) {
                notify(t('team_planned', 'Planned'), true);
                setRemark('');
                setClient(null);
                if (onChanged) onChanged();
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    return (
        <Panel theme={theme} title={t('team_admin_install', 'Plan an installation or intervention')}>
            <ToggleButtonGroup exclusive size="small" value={kind} onChange={(e, v) => v && setKind(v)}>
                <ToggleButton value="INSTALL" sx={{ textTransform: 'none' }}>{kindLabel(t, 'INSTALL')}</ToggleButton>
                <ToggleButton value="INTERVENTION" sx={{ textTransform: 'none' }}>{kindLabel(t, 'INTERVENTION')}</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StaffSelect staff={staff} value={who} onChange={setWho} t={t} sx={{ flex: '1 1 180px', ...field }} />
                <TextField type="date" size="small" label={t('team_day_label', 'Day')} value={day} onChange={(e) => setDay(e.target.value)} sx={{ flex: '1 1 150px', ...field }}
                    slotProps={{ inputLabel: { shrink: true } }} />
            </Box>
            <ClientField t={t} token={token} value={client} onChange={setClient} />
            <TextField size="small" multiline minRows={2} maxRows={8} label={t('team_remark', 'Remark')} value={remark}
                onChange={(e) => setRemark(e.target.value)} inputProps={{ maxLength: 500 }} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t('team_install_note', 'Counts for the support occupancy when the day is less than a week away. Only management plans installations.')}
            </Typography>
            <Box><Button variant="contained" disableElevation disabled={!who || !day || busy} onClick={save} sx={{ textTransform: 'none' }}>{t('team_plan', 'Plan')}</Button></Box>
        </Panel>
    );
};

/** Half days and hours: not something staff can request. */
const HoursPanel = ({ t, theme, token, me, staff, notify, onChanged }) => {
    const [who, setWho] = useState('');
    const [kind, setKind] = useState('RECUP');
    const [day, setDay] = useState(addDays(me.today, 1));
    const [hoursValue, setHours] = useState('');
    const [special, setSpecial] = useState('KLEIN_VERLET');
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);

    const number = parseFloat(String(hoursValue).replace(',', '.'));
    const save = () => {
        setBusy(true);
        teamPost('enter', token, { staff_id: who, kind, from: day, to: day, hours: number, special_code: kind === 'SPECIAL' ? special : null, remark }).then((response) => {
            setBusy(false);
            if (response?.success) {
                notify(t('team_saved', 'Saved'), true);
                setHours('');
                setRemark('');
                if (onChanged) onChanged();
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    return (
        <Panel theme={theme} title={t('team_admin_hours', 'Half day or a number of hours')}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StaffSelect staff={staff} value={who} onChange={setWho} t={t} sx={{ flex: '1 1 180px', ...field }} />
                <TextField select size="small" label={t('team_kind', 'Kind')} value={kind} onChange={(e) => setKind(e.target.value)} sx={{ flex: '1 1 150px', ...field }}>
                    {['LEAVE', 'RECUP', 'SICK', 'SPECIAL'].map((k) => <MenuItem key={k} value={k}>{kindLabel(t, k)}</MenuItem>)}
                </TextField>
            </Box>
            {kind === 'SPECIAL' && (
                <TextField select size="small" label={t('team_special_kind', 'Kind of special leave')} value={special} onChange={(e) => setSpecial(e.target.value)} sx={field}>
                    {SPECIAL_CODES.map((c) => <MenuItem key={c} value={c}>{specialLabel(t, c)}</MenuItem>)}
                </TextField>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField type="date" size="small" label={t('team_day_label', 'Day')} value={day} onChange={(e) => setDay(e.target.value)} sx={{ flex: '1 1 150px', ...field }}
                    slotProps={{ inputLabel: { shrink: true } }} />
                <TextField size="small" label={t('team_hours', 'Hours')} value={hoursValue} onChange={(e) => setHours(e.target.value)} inputProps={{ inputMode: 'decimal' }}
                    sx={{ flex: '1 1 100px', ...field }} error={hoursValue !== '' && (isNaN(number) || number <= 0)} />
            </Box>
            <TextField size="small" multiline minRows={2} maxRows={8} inputProps={{ maxLength: 500 }} label={kind === 'SPECIAL' ? t('team_remark_special', 'Remark (required for special leave)') : t('team_remark', 'Remark')}
                value={remark} onChange={(e) => setRemark(e.target.value)} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t('team_hours_note', 'For example "Gianni · recup · 3 h · Fri 20 Nov". Whole days can also be tapped in the calendar. Balance and calendar follow at once.')}
            </Typography>
            <Box><Button variant="contained" disableElevation disabled={!who || !day || isNaN(number) || number <= 0 || busy} onClick={save} sx={{ textTransform: 'none' }}>{t('team_enter', 'Enter')}</Button></Box>
        </Panel>
    );
};

/** Move, cancel or add a home-working day for a person or a whole department. */
const HomePanel = ({ t, theme, token, me, staff, notify, onChanged }) => {
    const [scope, setScope] = useState('DEV');
    const [who, setWho] = useState('');
    const [action, setAction] = useState('MOVE');
    const [day, setDay] = useState(addDays(me.today, 1));
    const [to, setTo] = useState(addDays(me.today, 2));
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const save = () => {
        setBusy(true);
        teamPost('moveHome', token, {
            department: scope === 'PERSON' ? null : scope, staff_id: scope === 'PERSON' ? who : null,
            day, action, moved_to: action === 'MOVE' ? to : null, reason,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                notify(t('team_saved', 'Saved') + ': ' + response.data.staff.join(', '), true);
                setReason('');
                if (onChanged) onChanged();
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    return (
        <Panel theme={theme} title={t('team_admin_home', 'Move home working')}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField select size="small" label={t('team_for', 'For')} value={scope} onChange={(e) => setScope(e.target.value)} sx={{ flex: '1 1 160px', ...field }}>
                    <MenuItem value="DEV">{departmentLabel(t, 'DEV')}</MenuItem>
                    <MenuItem value="SUPPORT">{departmentLabel(t, 'SUPPORT')}</MenuItem>
                    <MenuItem value="PERSON">{t('team_one_person', 'One colleague')}</MenuItem>
                </TextField>
                {scope === 'PERSON' && <StaffSelect staff={staff} value={who} onChange={setWho} t={t} sx={{ flex: '1 1 180px', ...field }} />}
            </Box>
            <ToggleButtonGroup exclusive size="small" value={action} onChange={(e, v) => v && setAction(v)}>
                <ToggleButton value="MOVE" sx={{ textTransform: 'none' }}>{t('team_home_move', 'Move')}</ToggleButton>
                <ToggleButton value="CANCEL" sx={{ textTransform: 'none' }}>{t('team_home_cancel', 'Cancel')}</ToggleButton>
                <ToggleButton value="ADD" sx={{ textTransform: 'none' }}>{t('team_home_add', 'Extra day')}</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField type="date" size="small" label={action === 'ADD' ? t('team_day_label', 'Day') : t('team_home_day', 'Home day')} value={day}
                    onChange={(e) => setDay(e.target.value)} sx={{ flex: '1 1 150px', ...field }} slotProps={{ inputLabel: { shrink: true } }} />
                {action === 'MOVE' && (
                    <TextField type="date" size="small" label={t('team_home_to', 'Moves to')} value={to} onChange={(e) => setTo(e.target.value)}
                        sx={{ flex: '1 1 150px', ...field }} slotProps={{ inputLabel: { shrink: true } }} />
                )}
            </Box>
            <TextField size="small" label={t('team_reason', 'Reason')} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t('team_home_note', 'Rules: Heleen Wednesday, Els Monday and Thursday; development moves one weekday each month (switch on the first Monday), support the day after. Only management moves a home day.')}
            </Typography>
            <Box><Button variant="contained" disableElevation disabled={busy || !day || (scope === 'PERSON' && !who)} onClick={save} sx={{ textTransform: 'none' }}>{t('team_save', 'Save')}</Button></Box>
        </Panel>
    );
};

/** The shared Google calendar: on/off, and update it now (the same as the daily run). */
const GooglePanel = ({ t, theme, token, notify }) => {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        let alive = true;
        teamPost('calendarStatus', token).then((response) => {
            if (alive && response?.success) setStatus(response.data);
        });
        return () => { alive = false; };
    }, [token]);

    const sync = () => {
        setBusy(true);
        setResult(null);
        teamPost('calendarSync', token).then((response) => {
            setBusy(false);
            if (!response?.success) {
                notify(teamError(t, response), false);
                return;
            }
            setStatus(response.data);
            setResult(response.data);
            if (response.data.synced) {
                notify(response.data.errors
                    ? t('team_gcal_done_errors', { defaultValue: 'Updated, with {{n}} error(s).', n: response.data.errors })
                    : t('team_gcal_done', 'Google calendar updated.'), !response.data.errors);
            }
        });
    };

    const reason = status && !status.on ? ({
        no_calendar_id: t('team_gcal_no_id', 'No calendar id set (setting google_calendar_id).'),
        no_key_file: t('team_gcal_no_key', 'The service account key is not on the server (private/google/verlofplanner.json).'),
        bad_key_file: t('team_gcal_bad_key', 'The key file on the server is not a valid service account key.'),
    }[status.reason] || status.reason) : null;

    return (
        <Panel theme={theme} title={t('team_gcal_title', 'Shared Google calendar')}>
            {!status && <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>…</Typography>}
            {status && (
                <Typography sx={{ fontSize: 13.5 }}>
                    {status.on
                        ? <><b style={{ color: '#2E8B57' }}>{t('team_gcal_on', 'On')}</b> · {status.account}</>
                        : <><b style={{ color: '#B26F00' }}>{t('team_gcal_off', 'Off')}</b> · {reason}</>}
                </Typography>
            )}
            {status?.last_sync && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {t('team_gcal_last_sync', 'Last update')}: {status.last_sync.at}
                    {' · '}{status.last_sync.errors ? status.last_sync.errors + ' ' + t('team_gcal_errors_word', 'error(s)') : t('team_gcal_no_errors', 'no errors')}
                </Typography>
            )}
            {status?.last_error && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {t('team_gcal_last_error', 'Last error')}: {status.last_error.at} · {status.last_error.what} · {String(status.last_error.error || '').substring(0, 160)}
                </Typography>
            )}
            {result?.synced && (
                <Typography sx={{ fontSize: 13.5 }}>
                    {t('team_gcal_result', { defaultValue: '{{requests}} requests and {{weeks}} weeks of weekend duty checked.', requests: result.requests, weeks: result.weeks })}
                </Typography>
            )}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t('team_gcal_note', 'Every change goes to the calendar at once, and the daily run puts everything right. Use this button for the first fill or after a Google outage.')}
            </Typography>
            <Box><Button variant="contained" disableElevation disabled={busy || !status?.on} onClick={sync} sx={{ textTransform: 'none' }}>
                {busy ? t('tc_busy', 'One moment…') : t('team_gcal_sync', 'Update now')}
            </Button></Box>
        </Panel>
    );
};

export default TeamAdminTab;
